import React, { useState, useEffect } from "react";
import { Container, Form, Button, Alert, Card } from "react-bootstrap";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { notify } = useNotification();

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, [searchParams]);

  // Request reset link (no token in URL)
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send reset link");
      }

      notify("Password reset link sent! Check your email.", "success");
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset password (token present in URL)
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          newPassword: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Dual-mode: email request form vs password reset form
  if (!token) {
    return (
      <Container className="d-flex justify-content-center align-items-center auth-container auth-bg">
        <Card className="shadow p-4 border-0 auth-card">
          <h2 className="text-center fw-bold mb-3 section-heading">Forgot Password</h2>
          <p className="text-center text-muted mb-3">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && (
            <Alert variant="success">
              Check your email for the reset link.
            </Alert>
          )}

          {!success && (
            <Form onSubmit={handleRequestReset}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="shadow-sm"
                />
              </Form.Group>

              <Button
                type="submit"
                className="w-100 fw-bold shadow-sm btn-brand"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </Form>
          )}

          <div className="text-center mt-3">
            <small className="text-muted">
              Remember your password?{" "}
              <Link to="/login" className="text-decoration-none">Login here</Link>
            </small>
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="d-flex justify-content-center align-items-center auth-container auth-bg">
      <Card className="shadow p-4 border-0 auth-card">
        <h2 className="text-center fw-bold mb-3 section-heading">Reset Password</h2>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && (
          <Alert variant="success">
            Password reset successful! Redirecting to login...
          </Alert>
        )}

        {!success && (
          <Form onSubmit={handleResetPassword}>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="shadow-sm"
                minLength="6"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="shadow-sm"
                minLength="6"
              />
            </Form.Group>

            <Button
              type="submit"
              className="w-100 fw-bold shadow-sm btn-brand"
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </Form>
        )}

        <div className="text-center mt-3">
          <small className="text-muted">
            Remember your password?{" "}
            <Link to="/login" className="text-decoration-none">Login here</Link>
          </small>
        </div>
      </Card>
    </Container>
  );
};

export default ResetPassword;
